// Passage de temoin entre le depart vers Apple/Google et le retour sur la page
// d'invitation.
//
// Le probleme tient en une ligne : oauthRedirectTo() renvoie sur
// `origin + pathname`, sans query. Le `?invite=CODE` qui a fait monter la page
// (index.js) n'existe donc plus au retour, et sans lui l'invite retomberait sur
// l'app ou sur la gate navigateur, connecte mais sans rien avoir debloque.
// L'URL de retour n'est pas negociable : elle doit figurer telle quelle dans les
// "Redirect URLs" du projet Supabase, on ne peut pas y accrocher le code.
//
// sessionStorage et pas localStorage : la duree d'un onglet est exactement celle
// de l'aller-retour. Un code oublie la, qui survivrait a la fermeture, viendrait
// se greffer sur un retour OAuth etranger (connexion depuis les reglages) et
// racheterait dans le dos de l'utilisateur.

const KEY = 'upscale_invite_oauth';

// Retours de linkIdentity qui veulent dire "ce compte appartient deja a un
// user" : le rattachement est impossible, seule la connexion pure aboutit.
// Meme liste que useOAuthReturn, partagee ici pour ne pas la voir diverger.
export const LINK_TAKEN_CODES = ['email_exists', 'identity_already_exists'];

export function rememberInviteForOAuth({ code, token = '', provider }) {
  if (!code) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ code, token, provider }));
  } catch {
    // Stockage indisponible : le retour ne retrouvera pas le code et affichera
    // l'app. Rien n'est rachete en silence, c'est le repli acceptable.
  }
}

export function readRememberedInvite() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY));
    return parsed?.code ? parsed : null;
  } catch {
    return null;
  }
}

export function clearRememberedInvite() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // rien a nettoyer
  }
}

// GoTrue rend le code PKCE en query et ses erreurs tantot en query, tantot dans
// le fragment : on lit les deux (meme lecture que useOAuthReturn).
export function readOAuthReturn(location = window.location) {
  try {
    const params = new URLSearchParams(location.search || '');
    const hash = new URLSearchParams((location.hash || '').replace(/^#/, ''));
    const pick = (key) => params.get(key) || hash.get(key);
    const code = params.get('code') || '';
    const errorCode = pick('error_code') || '';
    const error = pick('error') || pick('error_description') || '';
    if (!code && !error && !errorCode) return null;
    return { code, error, errorCode };
  } catch {
    return null;
  }
}

const RETURN_KEYS = ['code', 'error', 'error_code', 'error_description'];

// Le retour ne doit pas survivre a un rechargement ni a un retour arriere : un
// code PKCE ne s'echange qu'une fois, et rejoue il rend une erreur alors que la
// connexion a reussi.
export function clearOAuthReturnFromUrl() {
  try {
    const url = new URL(window.location.href);
    RETURN_KEYS.forEach((key) => url.searchParams.delete(key));
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
    const keepHash = RETURN_KEYS.some((key) => hash.has(key)) ? '' : url.hash;
    window.history.replaceState({}, '', `${url.pathname}${url.search}${keepHash}`);
  } catch {
    // URL exotique : l'ecran est deja affiche, rien de critique.
  }
}

// Vrai retour d'invitation = une reponse OAuth dans l'URL ET un code mis de cote
// au depart. Les deux, jamais l'un sans l'autre : une reponse OAuth seule
// appartient a l'app, et un code seul veut dire que la personne est revenue en
// arriere sans aller au bout.
export function pendingInviteOAuthReturn(location = window.location) {
  if (!readOAuthReturn(location)) return null;
  return readRememberedInvite();
}
